import { useState, useEffect, useRef } from 'react';
import './DateTimeInput.css';

const DateTimeInput = ({ value, onChange, label, required = false }) => {
  const datePickerRef = useRef(null);

  // Convertir la valeur ISO en date/heure séparées
  const parseDateTime = (isoString) => {
    if (!isoString) {
      const now = new Date();
      return {
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().slice(0, 5)
      };
    }
    const [date, time] = isoString.split('T');
    return {
      date: date,
      time: time ? time.slice(0, 5) : '00:00'
    };
  };

  const [dateTime, setDateTime] = useState(parseDateTime(value));

  useEffect(() => {
    setDateTime(parseDateTime(value));
  }, [value]);

  const handleDateChange = (newDate) => {
    const updated = { ...dateTime, date: newDate };
    setDateTime(updated);
    // Convertir au format ISO pour le parent
    const isoString = `${updated.date}T${updated.time}`;
    onChange(isoString);
  };

  const handleTimeChange = (newTime) => {
    const updated = { ...dateTime, time: newTime };
    setDateTime(updated);
    // Convertir au format ISO pour le parent
    const isoString = `${updated.date}T${updated.time}`;
    onChange(isoString);
  };

  // Convertir date YYYY-MM-DD en format français JJ/MM/AAAA
  const formatDateFr = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Convertir date JJ/MM/AAAA en YYYY-MM-DD
  const parseDateFr = (frDate) => {
    if (!frDate || frDate.length !== 10) return '';
    const [day, month, year] = frDate.split('/');
    return `${year}-${month}-${day}`;
  };

  const handleDateInputChange = (e) => {
    const input = e.target.value;
    // Permettre seulement les chiffres et /
    const cleaned = input.replace(/[^\d/]/g, '');

    // Auto-formater avec /
    let formatted = cleaned;
    if (cleaned.length >= 2 && cleaned[2] !== '/') {
      formatted = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 5 && cleaned[5] !== '/') {
      const parts = formatted.split('/');
      formatted = parts[0] + '/' + parts[1].slice(0, 2) + '/' + parts[1].slice(2);
    }

    e.target.value = formatted.slice(0, 10);

    // Si complet, convertir et envoyer
    if (formatted.length === 10) {
      const isoDate = parseDateFr(formatted);
      if (isoDate) {
        handleDateChange(isoDate);
      }
    }
  };

  const handleCalendarClick = () => {
    if (datePickerRef.current) {
      datePickerRef.current.showPicker();
    }
  };

  const handleDatePickerChange = (e) => {
    const newDate = e.target.value;
    handleDateChange(newDate);
  };

  const handleHourChange = (e) => {
    const newHour = e.target.value.padStart(2, '0');
    const [, minute] = dateTime.time.split(':');
    const newTime = `${newHour}:${minute || '00'}`;
    handleTimeChange(newTime);
  };

  const handleMinuteChange = (e) => {
    const newMinute = e.target.value.padStart(2, '0');
    const [hour] = dateTime.time.split(':');
    const newTime = `${hour || '00'}:${newMinute}`;
    handleTimeChange(newTime);
  };

  const [hour, minute] = dateTime.time.split(':');

  // Générer les options pour les heures (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  // Générer les options pour les minutes (0-59)
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  return (
    <div className="datetime-input-container">
      {label && <label className="datetime-label">{label}</label>}
      <div className="datetime-inputs">
        <div className="date-input-wrapper">
          <input
            type="text"
            className="date-input-fr"
            placeholder="JJ/MM/AAAA"
            value={formatDateFr(dateTime.date)}
            onChange={handleDateInputChange}
            maxLength="10"
            required={required}
          />
          <input
            ref={datePickerRef}
            type="date"
            className="date-picker-hidden"
            value={dateTime.date}
            onChange={handleDatePickerChange}
            tabIndex="-1"
          />
          <button
            type="button"
            className="calendar-icon-btn"
            onClick={handleCalendarClick}
            title="Ouvrir le calendrier"
          >
            📅
          </button>
        </div>
        <div className="time-selectors">
          <select
            className="time-select"
            value={hour}
            onChange={handleHourChange}
            required={required}
          >
            {hours.map(h => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
          <span className="time-separator">:</span>
          <select
            className="time-select"
            value={minute}
            onChange={handleMinuteChange}
            required={required}
          >
            {minutes.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default DateTimeInput;
